import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Paper,
  Grid,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Alert,
  CircularProgress,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Save';
import { useForm, useFieldArray, Controller } from 'react-hook-form';
import { invoicesApi, InvoiceLineItem, CreateInvoiceData } from '../services/invoices';
import { clientsApi, Client } from '../services/clients';
import { useCompany } from '../contexts/CompanyContext';
import { formatCurrency } from '../utils/currency';
import { format, addDays } from 'date-fns';

type InvoiceFormData = {
  clientId: string;
  issueDate: string;
  dueDate: string;
  lineItems: InvoiceLineItem[];
  notes?: string;
};

const InvoiceCreate: React.FC = () => {
  const navigate = useNavigate();
  const [clients, setClients] = useState<Client[]>([]);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { baseCurrency } = useCompany();

  const { register, handleSubmit, control, watch, formState: { errors } } = useForm<InvoiceFormData>({
    defaultValues: {
      clientId: '',
      issueDate: format(new Date(), 'yyyy-MM-dd'),
      dueDate: format(addDays(new Date(), 30), 'yyyy-MM-dd'),
      lineItems: [],
      notes: '',
    },
  });

  const { fields, append, remove, update } = useFieldArray({
    control,
    name: 'lineItems',
  });

  const watchedClientId = watch('clientId');
  const watchedLineItems = watch('lineItems');

  useEffect(() => {
    loadClients();
  }, []);

  useEffect(() => {
    if (watchedClientId) {
      const client = clients.find(c => c.id === watchedClientId);
      setSelectedClient(client || null);
      if (client && fields.length === 0) {
        // Add a default line item with client's charge rate
        append({
          description: '',
          quantity: 1,
          rate: client.chargeRate,
          amount: client.chargeRate,
        });
      }
    } else {
      setSelectedClient(null);
    }
  }, [watchedClientId, clients]);

  const loadClients = async () => {
    try {
      setLoading(true);
      const data = await clientsApi.getAll();
      setClients(data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load clients');
    } finally {
      setLoading(false);
    }
  };

  const calculateTotals = (items: InvoiceLineItem[]) => {
    const subtotal = items.reduce((sum, item) => {
      const amount = item.quantity * item.rate;
      return sum + amount;
    }, 0);
    const tax = 0; // Can be calculated based on tax config
    const total = subtotal + tax;
    return { subtotal, tax, total };
  };

  const handleAddLineItem = () => {
    append({
      description: '',
      quantity: 1,
      rate: selectedClient?.chargeRate || 0,
      amount: selectedClient?.chargeRate || 0,
    });
  };

  const handleLineItemChange = (index: number, field: keyof InvoiceLineItem, value: any) => {
    const currentItems = watchedLineItems || [];
    const item = { ...currentItems[index] };
    
    if (field === 'quantity' || field === 'rate') {
      item[field] = parseFloat(value) || 0;
      item.amount = item.quantity * item.rate;
    } else if (field === 'description') {
      item.description = value as string;
    } else if (field === 'amount') {
      // Amount is calculated, but handle it if needed
      item.amount = parseFloat(value) || 0;
    }
    
    update(index, item);
  };

  const onSubmit = async (data: InvoiceFormData) => {
    if (!selectedClient) {
      setError('Please select a client');
      return;
    }

    if (!data.lineItems || data.lineItems.length === 0) {
      setError('Please add at least one line item');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      const totals = calculateTotals(data.lineItems);
      
      const createData: CreateInvoiceData = {
        clientId: data.clientId,
        issueDate: data.issueDate,
        dueDate: data.dueDate,
        lineItems: data.lineItems.map(item => ({
          ...item,
          amount: item.quantity * item.rate,
        })),
        subtotal: totals.subtotal,
        tax: totals.tax,
        total: totals.total,
        currency: selectedClient.currency || baseCurrency,
        notes: data.notes || undefined,
        status: 'draft',
      };

      const invoice = await invoicesApi.create(createData);
      navigate(`/invoices/${invoice.id}`);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to create invoice');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  const totals = calculateTotals(watchedLineItems || []);

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Create New Invoice</Typography>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/invoices')}
        >
          Cancel
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <form onSubmit={handleSubmit(onSubmit)}>
        <Paper sx={{ p: 3, mb: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required error={!!errors.clientId}>
                <InputLabel>Client</InputLabel>
                <Controller
                  name="clientId"
                  control={control}
                  rules={{ required: 'Client is required' }}
                  render={({ field }) => (
                    <Select {...field} label="Client">
                      {clients.map((client) => (
                        <MenuItem key={client.id} value={client.id}>
                          {client.name}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                />
                {errors.clientId && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                    {errors.clientId.message}
                  </Typography>
                )}
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Issue Date"
                type="date"
                {...register('issueDate', { required: 'Issue date is required' })}
                error={!!errors.issueDate}
                helperText={errors.issueDate?.message}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Due Date"
                type="date"
                {...register('dueDate', { required: 'Due date is required' })}
                error={!!errors.dueDate}
                helperText={errors.dueDate?.message}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            {selectedClient && (
              <Grid item xs={12}>
                <Alert severity="info">
                  Client currency: {selectedClient.currency || baseCurrency} | 
                  Charge rate: {formatCurrency(selectedClient.chargeRate, selectedClient.currency || baseCurrency)}/hr
                </Alert>
              </Grid>
            )}
          </Grid>
        </Paper>

        {/* Line Items */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">Line Items</Typography>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleAddLineItem}
              disabled={!selectedClient}
            >
              Add Item
            </Button>
          </Box>

          {!selectedClient && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Please select a client first to add line items.
            </Alert>
          )}

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Description</TableCell>
                  <TableCell align="right" sx={{ width: '120px' }}>Quantity</TableCell>
                  <TableCell align="right" sx={{ width: '150px' }}>Rate</TableCell>
                  <TableCell align="right" sx={{ width: '150px' }}>Amount</TableCell>
                  <TableCell align="right" sx={{ width: '80px' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {fields.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">
                        No line items. {selectedClient ? 'Click "Add Item" to add one.' : 'Select a client first.'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  fields.map((field, index) => {
                    const item = watchedLineItems?.[index] || field;
                    return (
                      <TableRow key={field.id}>
                        <TableCell>
                          <TextField
                            fullWidth
                            size="small"
                            value={item.description || ''}
                            onChange={(e) => handleLineItemChange(index, 'description', e.target.value)}
                            placeholder="Item description"
                            required
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            fullWidth
                            size="small"
                            type="number"
                            value={item.quantity || 0}
                            onChange={(e) => handleLineItemChange(index, 'quantity', e.target.value)}
                            inputProps={{ min: 0, step: 0.01 }}
                            required
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            fullWidth
                            size="small"
                            type="number"
                            value={item.rate || 0}
                            onChange={(e) => handleLineItemChange(index, 'rate', e.target.value)}
                            inputProps={{ min: 0, step: 0.01 }}
                            InputProps={{
                              startAdornment: (
                                <Typography variant="body2" sx={{ mr: 1 }}>
                                  {selectedClient?.currency || baseCurrency}
                                </Typography>
                              ),
                            }}
                            required
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight="medium">
                            {formatCurrency(item.amount || 0, selectedClient?.currency || baseCurrency)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <IconButton
                            size="small"
                            onClick={() => remove(index)}
                            color="error"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Totals */}
          <Box display="flex" justifyContent="flex-end" mt={3}>
            <Box sx={{ minWidth: '250px' }}>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography variant="body1">Subtotal:</Typography>
                <Typography variant="body1">
                  {formatCurrency(totals.subtotal, selectedClient?.currency || baseCurrency)}
                </Typography>
              </Box>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography variant="body1">Tax:</Typography>
                <Typography variant="body1">
                  {formatCurrency(totals.tax, selectedClient?.currency || baseCurrency)}
                </Typography>
              </Box>
              <Box display="flex" justifyContent="space-between" mt={2} pt={2} sx={{ borderTop: '2px solid', borderColor: 'divider' }}>
                <Typography variant="h6" fontWeight="bold">
                  Total:
                </Typography>
                <Typography variant="h6" fontWeight="bold">
                  {formatCurrency(totals.total, selectedClient?.currency || baseCurrency)}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Paper>

        {/* Notes */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Notes
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            {...register('notes')}
            placeholder="Additional notes or terms..."
          />
        </Paper>

        {/* Actions */}
        <Box display="flex" justifyContent="flex-end" gap={2}>
          <Button
            variant="outlined"
            onClick={() => navigate('/invoices')}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            startIcon={<SaveIcon />}
            disabled={saving || !selectedClient || fields.length === 0}
          >
            {saving ? 'Creating...' : 'Create Invoice'}
          </Button>
        </Box>
      </form>
    </Box>
  );
};

export default InvoiceCreate;







